import { useEffect, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import type {
  PredictedCorners, R0RecordedReference,
} from "./multiRunOverlayAdapter";
import { buildMultiRunOverlayData } from "./multiRunOverlayAdapter";
import {
  ZOOM_GROUP, broadcastReset, broadcastZoom, isBroadcasting,
  joinZoomGroup, runSilently, type XRange, type ZoomMember,
} from "./zoomSync";
import { exportChartPng } from "./chartExport";

export type SelectedRunWaveform = {
  id: number;
  runNumber: number;
  intentLabel: string;
  predicted: PredictedCorners;
};

export type MultiRunOverlayChartProps = {
  timeS: number[];
  selectedRuns: SelectedRunWaveform[];
  r0RecordedReference: R0RecordedReference | null;
};

const CORNERS = ["lf", "rf", "lr", "rr"] as const;
const CORNER_LABEL = { lf: "LF", rf: "RF", lr: "LR", rr: "RR" } as const;
type CornerKey = typeof CORNERS[number];

// Alpha < 1 so overlapping traces blend instead of fully occluding each
// other — corners with no edit produce identical R0/R1 predictions, and
// at alpha 1.0 the later-drawn trace hides the earlier one completely.
const RUN_COLOURS = [
  "rgba(31, 119, 180, 0.85)",
  "rgba(255, 127, 14, 0.85)",
  "rgba(44, 160, 44, 0.85)",
  "rgba(214, 39, 40, 0.85)",
];
// First-selected run (typically R0) draws thicker; later runs are thinner
// so the earlier trace's edges remain visible as a halo where they overlap.
const RUN_WIDTH_BASE = 2.4;
const RUN_WIDTH_REST = 1.3;
const RECORDED_R0_COLOUR = "#777";
const LANE_HEIGHT = 160;
const MIN_LANE_WIDTH = 320;

type MembersRef = React.MutableRefObject<Record<CornerKey, ZoomMember | null>>;

function buildLaneOpts(
  corner: CornerKey,
  selectedRuns: SelectedRunWaveform[],
  hasReference: boolean,
  initialShowRecorded: boolean,
  yDomain: [number, number],
  width: number,
  membersRef: MembersRef,
): uPlot.Options {
  const series: uPlot.Series[] = [
    {} as uPlot.Series,
  ];
  selectedRuns.forEach((r, i) => {
    series.push({
      label: r.intentLabel,
      stroke: RUN_COLOURS[i % RUN_COLOURS.length],
      width: i === 0 ? RUN_WIDTH_BASE : RUN_WIDTH_REST,
    } as uPlot.Series);
  });
  if (hasReference) {
    series.push({
      label: "R0 recorded",
      stroke: RECORDED_R0_COLOUR,
      width: 1,
      dash: [4, 4],
      show: initialShowRecorded,
    } as uPlot.Series);
  }
  return {
    title: CORNER_LABEL[corner],
    width,
    height: LANE_HEIGHT,
    series,
    cursor: {
      // cursor.sync carries the crosshair; the zoom module carries the scale.
      sync: { key: ZOOM_GROUP },
      drag: { setScale: true, x: true, y: false },
      bind: {
        dblclick: (_self: uPlot) => (_e: MouseEvent) => {
          broadcastReset(ZOOM_GROUP);
          return null;
        },
      },
    },
    scales: {
      x: { time: false },
      y: { range: () => yDomain },
    },
    axes: [{}, {}],
    hooks: {
      setScale: [
        (self: uPlot, key: string) => {
          if (key !== "x" || isBroadcasting()) return;
          const member = membersRef.current[corner];
          if (!member) return;
          const s = self.scales.x;
          if (s?.min == null || s?.max == null) return;
          broadcastZoom(ZOOM_GROUP, member, { min: s.min, max: s.max });
        },
      ],
    },
  };
}

export function MultiRunOverlayChart(props: MultiRunOverlayChartProps) {
  const { timeS, selectedRuns, r0RecordedReference } = props;
  const [showRecordedR0, setShowRecordedR0] = useState(false);

  const lfRef = useRef<HTMLDivElement>(null);
  const rfRef = useRef<HTMLDivElement>(null);
  const lrRef = useRef<HTMLDivElement>(null);
  const rrRef = useRef<HTMLDivElement>(null);
  const containerRefs: Record<CornerKey, React.RefObject<HTMLDivElement>> = {
    lf: lfRef, rf: rfRef, lr: lrRef, rr: rrRef,
  };
  const plotRefs = useRef<Record<CornerKey, uPlot | null>>({
    lf: null, rf: null, lr: null, rr: null,
  });
  const mouseLeaveHandlers = useRef<Record<CornerKey, ((e: Event) => void) | null>>({
    lf: null, rf: null, lr: null, rr: null,
  });
  // Container for responsive width + the zoom-group members (one per lane).
  const rootRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<Record<CornerKey, ZoomMember | null>>({
    lf: null, rf: null, lr: null, rr: null,
  });
  // Latest time axis, read by a lane's reset (full range) without re-running
  // the mount effect on every data refresh.
  const timeSRef = useRef<number[]>(timeS);
  timeSRef.current = timeS;

  // Mount + cleanup
  useEffect(() => {
    const adapted = buildMultiRunOverlayData({
      time_s: timeS,
      runs: selectedRuns.map((r) => ({ id: r.id, predicted: r.predicted })),
      r0RecordedReference,
    });
    if (!adapted.ok) return;

    const hasRef = r0RecordedReference != null;
    const width = Math.max(
      MIN_LANE_WIDTH,
      Math.floor(rootRef.current?.getBoundingClientRect().width || 800),
    );
    const leaveFns: Array<() => void> = [];
    for (const corner of CORNERS) {
      const root = containerRefs[corner].current;
      if (!root) continue;
      const opts = buildLaneOpts(
        corner,
        selectedRuns,
        hasRef,
        false,
        adapted.yDomains[corner],
        width,
        membersRef,
      );
      // Construct silently so the initial setScale does not broadcast a zoom.
      let plot!: uPlot;
      runSilently(() => {
        plot = new uPlot(
          opts,
          adapted.data[corner] as unknown as uPlot.AlignedData,
          root,
        );
      });
      plotRefs.current[corner] = plot;

      const member: ZoomMember = {
        applyX: (range: XRange | null) => {
          const p = plotRefs.current[corner];
          if (!p) return;
          const t = timeSRef.current;
          const target = range ?? { min: t[0], max: t[t.length - 1] };
          runSilently(() => p.setScale("x", target));
        },
      };
      membersRef.current[corner] = member;
      leaveFns.push(joinZoomGroup(ZOOM_GROUP, member));

      const handler = (_e: Event) => {
        plot.setCursor({ left: -10, top: -10 });
      };
      plot.over.addEventListener("mouseleave", handler);
      mouseLeaveHandlers.current[corner] = handler;
    }

    // Responsive width: track the container and resize every lane together.
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.max(MIN_LANE_WIDTH, Math.floor(e.contentRect.width));
        for (const corner of CORNERS) {
          plotRefs.current[corner]?.setSize({ width: w, height: LANE_HEIGHT });
        }
      }
    });
    if (rootRef.current) ro.observe(rootRef.current);

    return () => {
      ro.disconnect();
      for (const fn of leaveFns) fn();
      for (const corner of CORNERS) {
        const p = plotRefs.current[corner];
        const h = mouseLeaveHandlers.current[corner];
        if (p && h) p.over.removeEventListener("mouseleave", h);
        if (p) p.destroy();
        plotRefs.current[corner] = null;
        mouseLeaveHandlers.current[corner] = null;
        membersRef.current[corner] = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRuns.map((r) => r.id).join(","),
    r0RecordedReference != null,
  ]);

  // Data sync on every data-shaped prop change.
  useEffect(() => {
    const adapted = buildMultiRunOverlayData({
      time_s: timeS,
      runs: selectedRuns.map((r) => ({ id: r.id, predicted: r.predicted })),
      r0RecordedReference,
    });
    if (!adapted.ok) return;
    for (const corner of CORNERS) {
      const p = plotRefs.current[corner];
      if (!p) continue;
      p.batch(() => {
        p.setData(adapted.data[corner] as unknown as uPlot.AlignedData, false);
        // Silent: re-pinning x to the full window on a data refresh must not
        // read as a user zoom to the rest of the group.
        runSilently(() => {
          p.setScale("x", { min: timeS[0], max: timeS[timeS.length - 1] });
          p.setScale("y", { min: adapted.yDomains[corner][0],
                            max: adapted.yDomains[corner][1] });
        });
      });
    }
  }, [timeS, selectedRuns, r0RecordedReference]);

  // Toggle effect — setSeries only, never setData.
  useEffect(() => {
    if (r0RecordedReference == null) return;
    for (const corner of CORNERS) {
      const p = plotRefs.current[corner];
      if (!p) continue;
      const recordedIdx = p.series.length - 1;
      p.setSeries(recordedIdx, { show: showRecordedR0 });
    }
  }, [showRecordedR0, r0RecordedReference]);

  const exportLanes = () => {
    for (const corner of CORNERS) {
      exportChartPng(plotRefs.current[corner], `overlay_${corner}.png`);
    }
  };

  return (
    <div className="multi-run-overlay" ref={rootRef}>
      <div className="multi-run-overlay-chrome">
        <button
          type="button"
          className="chart-export-btn"
          data-testid="multi-overlay-export-png"
          title="Export the four lanes as PNG"
          aria-label="Export the four lanes as PNG"
          onClick={exportLanes}
        >
          PNG
        </button>
        <button
          type="button"
          className="r0-recorded-toggle"
          disabled={r0RecordedReference == null}
          aria-pressed={showRecordedR0}
          onClick={() => setShowRecordedR0((v) => !v)}
          title={
            r0RecordedReference == null
              ? "R0 has no recorded waveform"
              : "Toggle R0 recorded reference"
          }
        >
          R0 recorded reference
        </button>
      </div>
      <div className="multi-run-overlay-lane" ref={lfRef} />
      <div className="multi-run-overlay-lane" ref={rfRef} />
      <div className="multi-run-overlay-lane" ref={lrRef} />
      <div className="multi-run-overlay-lane" ref={rrRef} />
    </div>
  );
}
